// The canonical permission registry (docs/clairvoyance/07-security.md §7.2).
// Every command and API declares exactly one key from PERMISSIONS.

export const PERMISSIONS = [
  // Cargo
  'cargo.view',
  'cargo.collect',
  'cargo.amend',
  'cargo.hold',
  'cargo.cancel',
  'cargo.return',
  // A tracking number typed by hand instead of the generated one (D-27)
  'cargo.tracking.custom',
  // Pickup
  'pickup.manage',
  'pickup.complete',
  // Stock
  'stock.manage',
  // Movement
  'trip.load',
  'trip.unload',
  'container.manage',
  'container.load',
  'customs.manage',
  // Final mile
  'finalmile.plan',
  'finalmile.deliver',
  // Partners
  'partner.manage',
  'partner.handover',
  'partner.update',
  'config.coverage.edit',
  // Exceptions and claims
  'exception.raise',
  'exception.resolve',
  'claims.manage',
  // Finance
  'finance.till',
  'finance.payment.record',
  'finance.cash.receive',
  'finance.refund',
  'finance.expense.record',
  'finance.bill.record',
  'finance.bill.pay',
  'finance.bank.manage',
  'finance.day.close',
  'finance.intercompany',
  'finance.fx',
  'finance.writeoff',
  'finance.adjust',
  'finance.period.close',
  'finance.reports.view',
  'finance.planning',
  'finance.approve',
  'finance.invoice.credit',
  'finance.einvoice.manage',
  // Config
  'config.zoning.edit',
  'config.pricing.edit',
  'config.branch.edit',
  'config.barcodes.manage',
  'config.time.edit',
  // Tracking number series and agents (03 §3.5, D-27)
  'config.numbering.edit',
  // People and data
  'users.manage',
  'customers.manage',
  'customers.care',
  'customers.merge',
  'kyc.view',
  'marketing.manage',
  // The staff directory (07 §7.6): salaries, service and visas. Never an AI worker's.
  'hr.view',
  'hr.manage',
  // Alerts
  'alerts.respond',
  'alerts.manage',
  // Customer app
  'community.moderate',
  'rewards.adjust',
  // Management
  'dashboard.view',
  // Help and training (18): take the in-app walkthroughs. Every standard role holds it.
  'help.training',
  // Development tools (local and staging only)
  'dev.tools',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

const PERMISSION_SET: ReadonlySet<string> = new Set(PERMISSIONS);

export function isPermission(value: string): value is Permission {
  return PERMISSION_SET.has(value);
}

/** Who issued a command. Only staff write `commands` directly; the rest go through server functions. */
export type Principal =
  | { kind: 'staff'; uid: string }
  | { kind: 'customer'; uid: string }
  | { kind: 'partner'; handoverId: string }
  | { kind: 'adapter'; partnerId: string }
  /** A visitor of the public website whose phone was verified by a one-time code (16 §16.2). */
  | { kind: 'website'; phone: string }
  | { kind: 'system'; job: string };

export type PrincipalKind = Principal['kind'];

export function principalId(p: Principal): string {
  switch (p.kind) {
    case 'staff':
    case 'customer':
      return `${p.kind}:${p.uid}`;
    case 'partner':
      return `partner:${p.handoverId}`;
    case 'adapter':
      return `adapter:${p.partnerId}`;
    case 'website':
      return `website:${p.phone}`;
    case 'system':
      return `system:${p.job}`;
  }
}

/** `all` or a list of sector and branch codes. An empty scope means no access. */
export type Scope = 'all' | { sectors: readonly string[]; branches: readonly string[] };

export interface RoleDefinition {
  id: string;
  name: string;
  permissions: readonly Permission[];
}

export interface StaffAccess {
  active: boolean;
  roleIds: readonly string[];
  scope: Scope;
}

/** Resolve the permissions a staff member holds from their roles. Unknown roles grant nothing. */
/**
 * Held by everyone with at least one role, whatever the role says: taking the walkthroughs of the
 * pages they can open (18 §18.4). A custom role, or a role saved before the permission existed,
 * would otherwise refuse a person their own training.
 */
export const EVERY_STAFF_PERMISSIONS: readonly Permission[] = ['help.training'];

export function resolvePermissions(
  roleIds: readonly string[],
  roles: ReadonlyMap<string, RoleDefinition>,
): ReadonlySet<Permission> {
  const out = new Set<Permission>();
  for (const id of roleIds) {
    const role = roles.get(id);
    if (!role) continue;
    for (const p of role.permissions) out.add(p);
    for (const p of EVERY_STAFF_PERMISSIONS) out.add(p);
  }
  return out;
}

export function hasPermission(
  access: StaffAccess,
  permission: Permission,
  roles: ReadonlyMap<string, RoleDefinition>,
): boolean {
  if (!access.active) return false;
  return resolvePermissions(access.roleIds, roles).has(permission);
}

/** True when the scope covers the branch (and its sector, when given). Empty scope is deny. */
export function scopeCovers(
  scope: Scope,
  target: { branchId?: string; sectorId?: string },
): boolean {
  if (scope === 'all') return true;
  if (target.branchId !== undefined && scope.branches.includes(target.branchId)) return true;
  if (target.sectorId !== undefined && scope.sectors.includes(target.sectorId)) return true;
  return false;
}

const ALL = PERMISSIONS.filter((p) => p !== 'dev.tools');

/** Standard roles (editable by a super admin later). 07 §7.2. */
export const STANDARD_ROLES: readonly RoleDefinition[] = [
  { id: 'super_admin', name: 'Super admin', permissions: ALL },
  {
    id: 'director',
    name: 'Director',
    permissions: [
      'dashboard.view',
      'finance.reports.view',
      'cargo.view',
      'alerts.respond',
      'alerts.manage',
      'finance.approve',
      'config.numbering.edit',
      'hr.view',
      'help.training',
    ],
  },
  {
    id: 'group_finance',
    name: 'Group finance',
    permissions: ALL.filter(
      (p) =>
        p.startsWith('finance.') ||
        p === 'dashboard.view' ||
        p === 'cargo.view' ||
        p === 'alerts.respond' ||
        p === 'help.training',
    ),
  },
  {
    id: 'sector_accountant',
    name: 'Sector accountant',
    permissions: [
      'cargo.view',
      'finance.payment.record',
      'finance.cash.receive',
      'finance.expense.record',
      'finance.bill.record',
      'finance.bill.pay',
      'finance.bank.manage',
      'finance.day.close',
      'finance.reports.view',
      'finance.planning',
      'finance.invoice.credit',
      'finance.refund',
      'alerts.respond',
      'help.training',
    ],
  },
  {
    id: 'branch_manager',
    name: 'Branch manager',
    permissions: [
      'cargo.view',
      'cargo.collect',
      'cargo.amend',
      'cargo.hold',
      'cargo.cancel',
      'cargo.return',
      'cargo.tracking.custom',
      'pickup.manage',
      'finalmile.plan',
      'finalmile.deliver',
      'stock.manage',
      'trip.load',
      'trip.unload',
      'container.manage',
      'container.load',
      'partner.handover',
      'partner.update',
      'exception.raise',
      'exception.resolve',
      'finance.till',
      'finance.payment.record',
      'finance.cash.receive',
      'finance.expense.record',
      'finance.day.close',
      'finance.approve',
      'customers.manage',
      'customers.care',
      'customers.merge',
      'alerts.respond',
      'alerts.manage',
      'dashboard.view',
      'help.training',
    ],
  },
  {
    id: 'cashier',
    name: 'Cashier',
    permissions: [
      'cargo.view',
      'cargo.collect',
      'cargo.tracking.custom',
      'finance.till',
      'finance.payment.record',
      'finance.cash.receive',
      'customers.manage',
      'exception.raise',
      'alerts.respond',
      'help.training',
    ],
  },
  {
    id: 'dispatcher',
    name: 'Dispatcher',
    permissions: [
      'cargo.view',
      'pickup.manage',
      'finalmile.plan',
      'finalmile.deliver',
      'exception.raise',
      'alerts.respond',
      'help.training',
    ],
  },
  {
    id: 'warehouse',
    name: 'Warehouse',
    permissions: [
      'cargo.view',
      'trip.load',
      'trip.unload',
      'container.load',
      'partner.handover',
      'exception.raise',
      'stock.manage',
      'alerts.respond',
      'help.training',
    ],
  },
  {
    id: 'driver',
    name: 'Driver',
    permissions: [
      'cargo.view',
      'pickup.complete',
      'finalmile.deliver',
      'exception.raise',
      'alerts.respond',
      'help.training',
    ],
  },
  {
    id: 'partner_coordinator',
    name: 'Partner coordinator',
    permissions: [
      'cargo.view',
      'partner.handover',
      'partner.update',
      'partner.manage',
      'exception.raise',
      'exception.resolve',
      'alerts.respond',
      'help.training',
    ],
  },
  {
    id: 'customer_support',
    name: 'Customer support',
    permissions: [
      'cargo.view',
      'customers.manage',
      'customers.care',
      'alerts.respond',
      'help.training',
    ],
  },
  {
    id: 'community_moderator',
    name: 'Community moderator',
    permissions: ['community.moderate', 'rewards.adjust', 'help.training'],
  },
];

export function standardRoleMap(): ReadonlyMap<string, RoleDefinition> {
  return new Map(STANDARD_ROLES.map((r) => [r.id, r]));
}
